import { useContext, useEffect, useRef } from 'react';
import { gsap, Power3, Power1 } from 'gsap';
import CSSPlugin from 'gsap/CSSPlugin';
import { LoadingContext } from '../../../context/LoadingContext';

gsap.registerPlugin(CSSPlugin);

const effects = {
  opacity: {
    from: { opacity: 0, y: 20 },
    to: { opacity: 1, y: 0 },
    duration: 0.65,
    ease: Power3.easeIn,
  },
  fadeInLeft: {
    from: { opacity: 0, x: -75 },
    to: { opacity: 1, x: 0 },
    duration: 0.65,
    ease: Power3.easeIn,
  },
  fadeIn: {
    from: { opacity: 0, x: 0 },
    to: { opacity: 1, x: 0 },
    duration: 0.5,
    ease: Power3.easeIn,
  },
  fadeInY: {
    from: { opacity: 0, y: 75 },
    to: { opacity: 1, y: 0 },
    duration: 0.55,
    ease: Power3.easeIn,
  },
  fadeInOnce: {
    to: { opacity: 1, y: 0 },
    duration: 0.55,
    ease: Power1.easeIn,
  },
};

export default function AnimationEffects(elements, items, animationName, fromDelay, toDelay) {
  const loaderDone = useContext(LoadingContext);

  // Refs must exist synchronously: consumers index singleRefs.elements[name].ref
  // and singleRefs.items[i].ref during the same render this hook is called in.
  const refsStore = useRef({ elements: {}, items: [] });
  const animatedNodes = useRef(new Set());

  const singleRefs = refsStore.current;
  elements.forEach((name) => {
    if (!singleRefs.elements[name]) {
      singleRefs.elements[name] = { ref: { current: null }, ratio: 0.4 };
    }
  });
  for (let i = singleRefs.items.length; i < items.length; i += 1) {
    singleRefs.items.push({ ref: { current: null }, ratio: 0.4 });
  }

  const elementsCount = elements.length;
  const itemsCount = items.length;

  useEffect(() => {
    const animation = effects[animationName];
    // Wait for the page loader to hide, otherwise entrance animations
    // play invisibly behind the overlay.
    if (!loaderDone || !animation) return undefined;

    const animated = animatedNodes.current;

    const observer = new IntersectionObserver((entries) => {
      let delay = fromDelay;
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        if (animated.has(entry.target)) return;
        animated.add(entry.target);
        const toVars = { ...animation.to, duration: animation.duration, ease: animation.ease, delay };
        if (animation.from) {
          gsap.fromTo(entry.target, { ...animation.from }, toVars);
        } else {
          // fadeInOnce has no "from": animate from the current CSS state (.opacity-0)
          gsap.to(entry.target, toVars);
        }
        delay += toDelay;
      });
    }, { threshold: 0.35 });

    const observeItem = (item) => {
      if (item && item.ref.current && !animated.has(item.ref.current)) {
        observer.observe(item.ref.current);
      }
    };
    Object.values(singleRefs.elements).forEach(observeItem);
    singleRefs.items.forEach(observeItem);

    return () => observer.disconnect();
  }, [loaderDone, animationName, fromDelay, toDelay, elementsCount, itemsCount, singleRefs]);

  return { singleRefs };
}
